/*
<summary>
   This functional component is all about rendering the Return Tool popup — showing the
   current loan record's details (fetched via Get Tool Loan Details By Tool Id) and submitting
   the actual return date via the Update Tool Loan Details API.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../../../contexts/store-provider';
import LoanStatusEnum from '../../../constants/enums/loan-status-enum';
import { getUserId, getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { formatMessage } from '../../../translations/format-message';
import ReturnToolForm, { IReturnToolFormValues } from '../forms/return-tool-form';

interface IProps {
  toolId: number;
  toolManagementNo: string;
  loanDate: string | null;
  tagId: string;
  borrowerFactoryId: number;
  modalClosed: () => void;
  onSuccess: () => void;
}

/**
* This functional component renders the Return Tool form (loan details + actual return date)
* and submits the return request for the selected tool.
* @param props: Tool identifier, tool management number, loan date, tag id and modal close handler
*/
const ReturnTool = (props: IProps) => {
  const { toolManagementStore, factoryStore } = useStore();
  const {
    ReturnToolService,
    resetReturnToolState,
    returnToolState,
    GetToolLoanDetailsByToolIdService,
    toolLoanDetails,
    toolLoanDetailsState,
    resetToolLoanDetailsState
  } = toolManagementStore;
  const { allFactorys } = factoryStore;

  /**
   * This useEffect is used to fetch the tool's current loan record as soon as the popup opens.
   */
  useEffect(() => {
    GetToolLoanDetailsByToolIdService(props.toolId);
  }, []);

  // Return date defaults to today; LoanDate is only carried along so the
  // form/schema can validate ReturnDate is not earlier than it.
  const initialValues: IReturnToolFormValues = {
    LoanDate: props.loanDate,
    ReturnDate: new Date(),
  };

  useEffect(() => {
    if (returnToolState.success) {
      toast.success(formatMessage('tool_returned_success'));
      resetReturnToolState();
      resetToolLoanDetailsState();
      props.modalClosed();
      props.onSuccess();
    }
  }, [returnToolState.success]);

  useEffect(() => {
    if (returnToolState.error) {
      toast.error(formatMessage(returnToolState.error));
      resetReturnToolState();
    }
  }, [returnToolState.error]);

    /**
  * This function is used to look up a factory's DB-level FactoryId 
  * @param tenantId: Factory's numeric TenantId
  */
  const getDbFactoryName = (tenantId: number | null): string =>
    (tenantId != null && allFactorys.find((f) => Number(f.TenantId) === tenantId)?.FactoryId) || '';


  const onSubmitForm = (values: IReturnToolFormValues) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    const returnDate =
      values.ReturnDate instanceof Date
        ? `${values.ReturnDate.getFullYear()}-${pad(values.ReturnDate.getMonth() + 1)}-${pad(
            values.ReturnDate.getDate()
          )}`
        : String(values.ReturnDate || '');
    ReturnToolService({
      ToolId: props.toolId,
      ToolNumber: props.toolManagementNo,
      DbBorrowerFactoryName: getDbFactoryName(props.borrowerFactoryId),
      ActualReturnDate: returnDate,
      LoanStatus: LoanStatusEnum.Returned,
      ReturnedBy: Number(getUserId()) || 0,
      CurrentTagId: props.tagId,
      TenantId: Number(getTenantIdFromLocalStorage()),
    });
  };

  const onModalClosed = () => {
    resetToolLoanDetailsState();
    props.modalClosed();
  };

  return (
    <ReturnToolForm
      toolManagementNo={props.toolManagementNo}
      loanDetails={toolLoanDetails}
      initialValues={initialValues}
      isLoading={returnToolState.inProgress || toolLoanDetailsState.inProgress}
      onModalClosed={onModalClosed}
      submitHandler={onSubmitForm}
    />
  );
};

export default observer(ReturnTool);
